import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Phone, MessageSquare, Trash2, Plus, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { tel } from "@/lib/helplines";

const EMPTY = { name: "", phone: "", relation: "" };

function ContactForm({ initial, onDone }: { initial?: any; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState(initial ? { name: initial.name, phone: initial.phone, relation: initial.relation } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await (initial ? api.patch(`/me/trusted-contacts/${initial.id}`, f) : api.post("/me/trusted-contacts", f));
      qc.invalidateQueries({ queryKey: ["trusted"] });
      toast.success("Contatto salvato");
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="p-3 space-y-2" data-testid="trusted-form">
      <input className="field" placeholder="Nome" maxLength={50} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Nome" data-testid="trusted-name-input" />
      <input className="field" type="tel" inputMode="tel" placeholder="Telefono, es. +39 333 123 4567" maxLength={24} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} aria-label="Telefono" data-testid="trusted-phone-input" />
      <input className="field" placeholder="Chi è per te (facoltativo), es. sorella, amico" maxLength={40} value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} aria-label="Relazione" data-testid="trusted-relation-input" />
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy || !f.name.trim() || !f.phone.trim()} data-testid="trusted-save-btn">Salva</button>
        <button type="button" className="btn btn-ghost" onClick={onDone} data-testid="trusted-cancel-btn">Annulla</button>
      </div>
    </form>
  );
}

function ContactRow({ c, onEdit }: { c: any; onEdit: () => void }) {
  const qc = useQueryClient();
  const remove = async () => {
    if (!window.confirm(`Eliminare ${c.name} dai contatti di fiducia?`)) return;
    try { await api.delete(`/me/trusted-contacts/${c.id}`); qc.invalidateQueries({ queryKey: ["trusted"] }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="row" data-testid="trusted-item">
      <span className="flex-1 min-w-0"><span className="font-semibold block truncate">{c.name}</span><span className="text-xs text-ink/60">{c.relation ? `${c.relation} · ` : ""}{c.phone}</span></span>
      <a href={tel(c.phone)} className="icon-btn" aria-label={`Chiama ${c.name}`} data-testid="trusted-call-btn"><Phone size={17} /></a>
      <a href={`sms:${c.phone.replace(/[^\d+]/g, "")}`} className="icon-btn" aria-label={`Scrivi un SMS a ${c.name}`} data-testid="trusted-sms-btn"><MessageSquare size={17} /></a>
      <button className="icon-btn" onClick={onEdit} aria-label="Modifica" data-testid="trusted-edit-btn"><Pencil size={16} /></button>
      <button className="icon-btn" onClick={remove} aria-label="Elimina" data-testid="trusted-delete-btn"><Trash2 size={16} /></button>
    </div>
  );
}

export function TrustedContacts() {
  const { data, isLoading } = useQuery({ queryKey: ["trusted"], queryFn: () => api.get("/me/trusted-contacts").then((r) => r.data) });
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <section className="space-y-2" data-testid="trusted-contacts">
      <div className="px-1">
        <h2 className="font-semibold">I tuoi contatti di fiducia</h2>
        <p className="text-sm text-ink/66 inline-flex items-center gap-1.5"><Lock size={13} />Visibili solo a te. Nessuno viene avvisato o contattato in automatico.</p>
      </div>
      <div className="surface p-1">
        {isLoading ? null : !data?.length && editing !== "new" && <div className="row text-sm text-ink/62" data-testid="trusted-empty">Aggiungi una o più persone da chiamare quando ne hai bisogno.</div>}
        {(data || []).map((c: any) => editing === c.id ? <ContactForm key={c.id} initial={c} onDone={() => setEditing(null)} /> : <ContactRow key={c.id} c={c} onEdit={() => setEditing(c.id)} />)}
        {editing === "new" ? <ContactForm onDone={() => setEditing(null)} /> : (data?.length ?? 0) < 10 && (
          <button className="row rounded-2xl font-semibold" onClick={() => setEditing("new")} data-testid="trusted-add-btn"><Plus size={17} className="text-[hsl(var(--primary))]" />Aggiungi un contatto</button>
        )}
      </div>
    </section>
  );
}
